import React, { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { bankComplete, getProfile } from "@/lib/api";
import { closeAuthBrowser, onDeepLink, parseBankCallback } from "@/lib/native";
import { initPush } from "@/lib/push";
import { AnalyzingOverlay } from "@/components/AnalyzingOverlay";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Transactions from "@/pages/Transactions";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import "@/App.css";

function Shell({ children, wide = false }) {
  // `wide`: solo sulla Home (utente onboarded) il frame si allarga su desktop per
  // ospitare il layout "cockpit" a 3 colonne. Su mobile resta sempre 430px.
  // Login/Onboarding/legali restano colonna stretta centrata su ogni dimensione.
  const frame = wide
    ? "max-w-[430px] lg:max-w-[1320px] bg-[#050508] lg:bg-transparent border-x border-white/[0.04] lg:border-x-0"
    : "max-w-[430px] bg-[#050508] border-x border-white/[0.04]";
  return (
    <div className="min-h-dvh bg-[#030305] relative liqo-scanlines">
      <div className="pointer-events-none fixed inset-0 liqo-vignette" />
      <div className={`relative mx-auto w-full min-h-dvh ${frame}`}>
        {children}
      </div>
    </div>
  );
}

function Splash() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3" data-testid="app-splash">
      <div className="mono flex items-center text-3xl font-light tracking-[-0.02em] text-white">
        LIQO<span className="liqo-blink ml-1 text-[#00FF87]">▌</span>
      </div>
      <p className="caps-tag text-white/30">Il tuo CFO AI</p>
    </div>
  );
}

function AppRoutes() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [completingBank, setCompletingBank] = useState(false);

  // Frame largo (cockpit desktop) su Home e Transazioni di un utente onboarded.
  const wide =
    isAuthenticated &&
    !!profile?.onboarded &&
    (location.pathname === "/" || location.pathname === "/transactions");

  const loadProfile = useCallback(async () => {
    try {
      const p = await getProfile();
      setProfile(p);
    } catch (e) {
      setProfile({ onboarded: false });
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  // Gestisce il ritorno dal consenso Enable Banking: <origin>/?code=...&state=...
  // `input` opzionale: URL completo del deep link nativo o query string; sul web
  // di default usa window.location.search.
  const handleBankCallback = useCallback(
    async (input) => {
      const source = input != null ? input : window.location.search;
      const { code, state, error } = parseBankCallback(source);

      // È un callback bancario solo se c'è un code (o un errore con state)
      if (!code && !(error && state)) return false;

      const cleanUrl = () => window.history.replaceState({}, "", window.location.pathname);

      if (error) {
        cleanUrl();
        toast.error("Collegamento bancario annullato o non riuscito");
        return false;
      }

      setCompletingBank(true);
      try {
        const res = await bankComplete({ code, state });
        cleanUrl();
        toast.success(
          `${res.institution_name || "Banca"} collegata · ${res.imported || 0} transazioni importate`
        );
        await loadProfile();
      } catch (e) {
        cleanUrl();
        toast.error(e?.response?.data?.detail || "Non sono riuscito a completare il collegamento");
        await loadProfile();
      } finally {
        setCompletingBank(false);
      }
      return true;
    },
    [loadProfile]
  );

  // Deep link nativo (Universal/App Link): ritorno dal consenso bancario nell'app.
  useEffect(() => {
    const off = onDeepLink(async (url) => {
      const handled = await handleBankCallback(url);
      if (handled) await closeAuthBrowser();
    });
    return off;
  }, [handleBankCallback]);

  // Carica il profilo SOLO quando l'utente è autenticato (le rotte /api sono protette).
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setProfile(null);
      setLoadingProfile(false);
      return;
    }
    setLoadingProfile(true);
    (async () => {
      const handled = await handleBankCallback();
      if (!handled) await loadProfile();
    })();
    // Registra le push native (no-op sul web): richiede permesso + invia token FCM.
    initPush();
  }, [authLoading, isAuthenticated, handleBankCallback, loadProfile]);

  // Mentre risolviamo lo stato di autenticazione (token / callback Google), mostra lo splash.
  if (authLoading) {
    return (
      <Shell>
        <Splash />
      </Shell>
    );
  }

  return (
    <>
      <Shell wide={wide}>
        {completingBank ? (
          <AnalyzingOverlay
            messages={[
              "Verifico il consenso bancario…",
              "Recupero i conti collegati…",
              "Importo le transazioni reali…",
              "Classifico ogni movimento…",
              "Genero i tuoi insight…",
            ]}
          />
        ) : (
          <Routes>
            {/* Rotte pubbliche (accessibili anche senza login) */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            {!isAuthenticated ? (
              <>
                <Route path="/login" element={<Login />} />
                <Route path="*" element={<Login />} />
              </>
            ) : (
              <>
                <Route
                  path="/"
                  element={
                    loadingProfile ? (
                      <Splash />
                    ) : profile?.onboarded ? (
                      <Home />
                    ) : (
                      <Onboarding onComplete={(p) => setProfile(p)} />
                    )
                  }
                />
                <Route
                  path="/transactions"
                  element={
                    loadingProfile ? (
                      <Splash />
                    ) : profile?.onboarded ? (
                      <Transactions />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
          </Routes>
        )}
      </Shell>
      <Toaster
        theme="dark"
        position="bottom-center"
        offset={92}
        duration={2500}
        toastOptions={{
          style: {
            background: "#0D0D14",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "8px",
            color: "rgba(255,255,255,0.92)",
            fontSize: "13px",
          },
        }}
      />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
